import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { format } from 'date-fns'
import { getBoundActions } from '../../../actions/index'
import GaiaLogo, { TYPE_WHITE } from '../../GaiaLogo/index'
import Link from '../../Link/index'
import { getPrimary } from '../../../services/languages/index'
import Button, { TYPES } from '../../Button/index'
import Sherpa, { TYPE_LARGE } from '../../Sherpa/index'
import { TARGET_BLANK } from '../../Link/constants'
import { SPECIAL_OFFER_DURATION_MONTHS } from '../../../services/user-account/index'
import {
  getCurrentTime,
  addMonths,
  getDateTime,
  ordinalDateFormatingi18n,
  getDateLocale,
  formatWithLocale,
} from '../../../services/date-time/index'
import { URL_ACCOUNT, URL_ACCOUNT_CANCEL_CONFIRM } from '../../../services/url/constants'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import { scrollToTop } from '../../../services/dom/index'
import {
  CANCEL_FLOW_FREE_MONTH_REJECTED_GA4_EVENT,
  CANCEL_FLOW_FREE_MONTH_SELECTED_GA4_EVENT,
  CANCEL_FLOW_FREE_MONTH_OFFER_SHOWN_GA4_EVENT,
} from '../../../services/event-tracking/index'
import AccountCancelButtons from '../AccountCancelButtons'

function AccountCancelOffer (props) {
  const {
    user,
    staticText,
    userAccount,
    subscription,
    setEventDefault,
    setUserAccountCancel,
    sendOptimizelyFeatureEvent,
  } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const dateFormatStringOrdinal = ordinalDateFormatingi18n(userLanguage)
  const dateLocale = getDateLocale(userLanguage)
  const paidThroughDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'], getCurrentTime())
  const offerEndDate = addMonths(paidThroughDate, SPECIAL_OFFER_DURATION_MONTHS)
  const reformattedPaidThroughDate = getDateTime(paidThroughDate)
  const reformattedOfferEndDate = getDateTime(offerEndDate)
  const formattedResumeEndDate = formatWithLocale(
    reformattedPaidThroughDate,
    dateFormatStringOrdinal,
    { locale: dateLocale },
  )
  const subscriptionError = subscription.get('errors')
  const subscriptionProcessing = subscription.get('processing')
  const subscriptionSuccess = subscription.get('success')
  const userAccountProcessing = userAccount.get('billingSubscriptionsProcessing')

  const checkCopyOne3 = String(staticText.get('checkCopyOne3'))
    .replace(/\$\{ beginDate \}/, format(reformattedPaidThroughDate, 'MMMM Do', { locale: dateLocale }))
    .replace(/\$\{ endDate \}/, format(reformattedOfferEndDate, 'MMMM Do', { locale: dateLocale }), { locale: dateLocale })
  const copy1 = <>{staticText.get('checkCopyOne1')}<span>{staticText.get('checkCopyOne2')}</span>{checkCopyOne3}</>
  const copy2 = <>{staticText.get('checkCopyTwo')}<span>{format(reformattedOfferEndDate, 'MMMM Do', { locale: dateLocale })}</span></>
  const copy3 = <>{staticText.get('checkCopyThree1')}<span>{staticText.get('checkCopyThree2')}</span></>

  useEffect(() => {
    // GA4 event
    setEventDefault(CANCEL_FLOW_FREE_MONTH_OFFER_SHOWN_GA4_EVENT)
  }, [])

  const onClickRedeemOffer = useCallback(() => {
    const { setUserComp } = props
    setUserComp()

    // GA4 event
    setEventDefault(CANCEL_FLOW_FREE_MONTH_SELECTED_GA4_EVENT)
    sendOptimizelyFeatureEvent({ eventName: 'cancel_flow_free_month_selected' })
  }, [])

  const onClickRejectOffer = () => {
    // GA4 event
    setEventDefault(CANCEL_FLOW_FREE_MONTH_REJECTED_GA4_EVENT)

    // Cancel Account
    setUserAccountCancel()
  }

  const renderErrorState = () => {
    return (
      <React.Fragment>
        <p className="account-cancel-offer__error">{staticText.get('errorMessage')}</p>
        <p className="account-cancel-offer__error-text">{staticText.get('errorMessageSubText1')}
          <Link
            to={'https://gaiasupportcenter.zendesk.com/hc/en-us'}
            directLink
            target={TARGET_BLANK}
            className="account-cancel__list-link"
          > {staticText.get('customerService')} </Link>
          {staticText.get('errorMessageSubText2')}</p>
        <Button
          onClick={onClickRedeemOffer}
          type={TYPES.PRIMARY}
          className="account-cancel-buttons__previous"
        >
          {staticText.get('retry')}
        </Button>
      </React.Fragment>
    )
  }

  const renderProcessingState = () => {
    return (
      <div className="account-cancel-offer__processing-container">
        <Sherpa className={['account-cancel-offer__sherpa']} type={TYPE_LARGE} />
        <p className="account-cancel-offer__processing">{staticText.get('processing')}</p>
      </div>
    )
  }

  const renderSubscriptionSuccessState = () => {
    scrollToTop()
    return <React.Fragment>
      <div className="account-cancel-offer__success-title">{staticText.get('title4')}</div>
      <div className="account-cancel-offer__success-subtitle">{staticText.get('subtitle')}</div>
      <div className="account-cancel-offer__success-body">
        {staticText.get('body2')}
        <span>{formattedResumeEndDate}</span>
        {staticText.get('body3')}
      </div>
      <div className="account-cancel-offer__success-my-account">
        <Link
          to={URL_ACCOUNT}
          className="account-cancel-offer__success-my-account-link"
        >
          {staticText.get('myAccount')}
          <IconV2
            className="account-cancel-offer__success-my-account-link-icon"
            type={ICON_TYPES.CHEVRON_RIGHT}
          />
        </Link>
      </div>
    </React.Fragment>
  }

  const handelErrorProccessingState = () => {
    if (subscriptionProcessing || userAccountProcessing) {
      return renderProcessingState()
    } if (subscriptionError && !subscriptionProcessing) {
      return renderErrorState()
    }

    return <>
      {renderOfferButton(
        TYPES.PRIMARY,
        'account-cancel-offer__button',
        staticText.get('keepMembership'),
      )}
      <AccountCancelButtons
        previousHidden={true}
        nextText={staticText.get('notIntenrested')}
        nextUrl={URL_ACCOUNT_CANCEL_CONFIRM}
        nextOnClick={onClickRejectOffer}
        nextDisabled={false}
        nextClass="account-cancel-offer__button"
        nextType={TYPES.SECONDARY}
      />
    </>
  }

  const renderCheckListItem = (copy) => {
    return (
      <div className="account-cancel-offer__item">
        <IconV2 className="account-cancel-offer__icon" type={ICON_TYPES.CHECK}/>
        <div className="account-cancel-offer__copy">{copy}</div>
      </div>
    )
  }

  const renderGaiaCta = () => {
    const logoClass = 'account-cancel-offer__cta-gaia-logo'
    return (
      <div className="account-cancel-offer__cta">
        <div className="account-cancel-offer__cta-label">
          {staticText.get('specialOffer')}
        </div>
        <div className="account-cancel-offer__cta-logo">
          <GaiaLogo
            type={TYPE_WHITE}
            className={[logoClass]}
            isHref={false}
          />
          <div className="account-cancel-offer__cta-blurb">
            {staticText.get('oneMonthFree')}
          </div>
        </div>
      </div>
    )
  }

  const renderOfferButton = (btnType, btnClass, btnCopy) => {
    if (subscriptionProcessing || userAccountProcessing) {
      return renderProcessingState()
    } if (subscriptionError && !subscriptionProcessing) {
      return renderErrorState()
    }

    return (
      <Button
        type={btnType}
        className={btnClass}
        onClick={onClickRedeemOffer}
      >
        {btnCopy}
      </Button>
    )
  }

  const renderCancelOffer = () => {
    return <div className="account-cancel-offer">
      {renderGaiaCta()}
      <div className="account-cancel-offer__disclaimer">{staticText.get('disclaimer')}</div>
      <div className="account-cancel-offer__title">{staticText.get('title3')}</div>
      <div className="account-cancel-offer__body">{staticText.get('body')}</div>
      <div className="account-cancel-offer__check-list">
        {renderCheckListItem(copy1)}
        {renderCheckListItem(copy2)}
        {renderCheckListItem(copy3)}
      </div>
      <div className="account-cancel-offer__button-container">
        {handelErrorProccessingState()}
      </div>
    </div>
  }

  return (
    !subscriptionSuccess || (subscriptionSuccess && userAccountProcessing)
      ? renderCancelOffer()
      : renderSubscriptionSuccessState()
  )
}

AccountCancelOffer.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  page: ImmutablePropTypes.map.isRequired,
  subscription: ImmutablePropTypes.map.isRequired,
  fullPage: PropTypes.bool,
  setUserComp: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      userAccount: state.userAccount,
      page: state.page,
      subscription: state.subscription,
      staticText: state.staticText.getIn(['data', 'accountCancelOffer', 'data']),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUserComp: actions.subscription.setUserComp,
        setEventDefault: actions.eventTracking.setEventDefault,
        setUserAccountCancel: actions.userAccount.setUserAccountCancel,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
      }
    },
  ),
)(AccountCancelOffer)
